import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AlertTriangle, Award, CheckCircle2, Download, ListChecks, Percent, Users } from 'lucide-react';
import { gestorService } from '../../shared/services';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function ManagerTeamPage() {
  const [busca, setBusca] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: equipe = [], isLoading, isError } = useQuery({
    queryKey: ['gestor-equipe'],
    queryFn: gestorService.getEquipe,
  });

  const exportar = useMutation({
    mutationFn: () => gestorService.exportarCsv(),
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível exportar a equipe.')),
  });

  const filtrada = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return equipe;

    return equipe.filter(
      (item) =>
        item.nome.toLowerCase().includes(termo)
        || item.email.toLowerCase().includes(termo)
        || item.areaNome.toLowerCase().includes(termo)
        || item.cargoNome.toLowerCase().includes(termo),
    );
  }, [equipe, busca]);

  const resumo = useMemo(() => {
    const colaboradores = equipe.length;
    const pendentes = equipe.reduce((total, item) => total + item.trilhasPendentes, 0);
    const atrasadas = equipe.reduce((total, item) => total + item.trilhasAtrasadas, 0);
    const media = colaboradores > 0
      ? Math.round(equipe.reduce((total, item) => total + item.percentualMedio, 0) / colaboradores)
      : 0;

    return { colaboradores, pendentes, atrasadas, media };
  }, [equipe]);

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users size={28} /> Meu Time
            </h1>
            <p className="text-gray-600 mt-1">
              Acompanhe o progresso, os prazos e os certificados dos seus subordinados diretos.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="Buscar colaborador, área ou cargo"
              className="border border-gray-300 rounded-lg px-3 py-2 w-72"
            />
            <button
              type="button"
              onClick={() => exportar.mutate()}
              disabled={exportar.isPending || equipe.length === 0}
              className="btn-secondary flex items-center gap-2 disabled:opacity-60"
            >
              <Download size={16} /> {exportar.isPending ? 'Gerando...' : 'Exportar CSV'}
            </button>
          </div>
        </div>

        {aviso && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{aviso}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : isError ? (
          <div className="card text-center py-10 text-red-600">Não foi possível carregar sua equipe.</div>
        ) : equipe.length === 0 ? (
          <div className="card text-center py-12">
            <Users className="mx-auto text-gray-400 mb-4" size={56} />
            <h2 className="text-xl font-semibold text-gray-900">Nenhum colaborador vinculado a você</h2>
            <p className="text-gray-600 mt-2 max-w-xl mx-auto">
              O gestor é definido no vínculo profissional. Peça ao RH para preencher o campo
              &quot;Gestor&quot; em <strong>Estrutura por Área</strong>.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { rotulo: 'Colaboradores', valor: resumo.colaboradores, Icon: Users },
                { rotulo: 'Trilhas pendentes', valor: resumo.pendentes, Icon: ListChecks },
                { rotulo: 'Trilhas atrasadas', valor: resumo.atrasadas, Icon: AlertTriangle },
                { rotulo: 'Progresso médio', valor: `${resumo.media}%`, Icon: Percent },
              ].map(({ rotulo, valor, Icon }) => (
                <div key={rotulo} className="card">
                  <Icon className="text-primary-600 mb-2" size={20} />
                  <p className="text-2xl font-bold text-gray-900">{valor}</p>
                  <p className="text-xs text-gray-600">{rotulo}</p>
                </div>
              ))}
            </div>

            <section className="card overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">Colaborador</th>
                    <th className="py-2 pr-4">Área / Cargo</th>
                    <th className="py-2 pr-4">Trilhas</th>
                    <th className="py-2 pr-4">Progresso</th>
                    <th className="py-2 pr-4">Atrasadas</th>
                    <th className="py-2 pr-4">Certificados</th>
                    <th className="py-2">Último acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrada.map((colaborador) => (
                    <tr key={colaborador.usuarioId} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 pr-4">
                        <Link to={`/gestor/${colaborador.usuarioId}`} className="font-medium text-primary-700 hover:underline">
                          {colaborador.nome}
                        </Link>
                        <p className="text-xs text-gray-500">{colaborador.email}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="text-gray-900">{colaborador.areaNome}</p>
                        <p className="text-xs text-gray-500">{colaborador.cargoNome}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1 text-gray-900">
                          <CheckCircle2 size={14} className="text-green-600" />
                          {colaborador.trilhasConcluidas}/{colaborador.totalTrilhas}
                        </span>
                      </td>
                      <td className="py-3 pr-4 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex-1">
                            <div className="h-full bg-primary-600" style={{ width: `${colaborador.percentualMedio}%` }} />
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-9 text-right">{colaborador.percentualMedio}%</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={colaborador.trilhasAtrasadas > 0 ? 'text-red-700 font-medium' : 'text-gray-500'}>
                          {colaborador.trilhasAtrasadas}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1 text-gray-700">
                          <Award size={14} /> {colaborador.certificados}
                        </span>
                      </td>
                      <td className="py-3 text-gray-600">{formatarData(colaborador.ultimoAcesso)}</td>
                    </tr>
                  ))}
                  {filtrada.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-4 text-gray-500">Nenhum colaborador encontrado para a busca.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
